import {
  alpha,
  Box,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useCallback, ReactNode } from "react";
import BookingNavBar from "@/components/booking/steps/bookingNavBar";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import {
  useBookingGoBackButton,
  useInvalidSelectiveBookingLinkRedirection,
} from "@/hooks/booking/useBookingConfirm";
import { DiscountType } from "@/types";
import { BookingStep } from "@/types/bookingTypes";
import { getDiscountDetailsText } from "@/utils/discounts";
import { isInvalidSelectiveBookingLink } from "@/utils/selectiveBookingLink";
import AdditionalInfo from "../common/additionalInfo/additionalInfo";
import { ArrowLeftIcon } from "../common/icons";

export enum NewBookingSteps {
  SELECT_SERVICES = "Select services(s)",
  SCHEDULE_APPOINTMENT = "Schedule appointment",
  ENTER_ACCOUNT_DETAILS = "Enter account details",
  COMPLETE_BOOKING = "Complete booking",
}

export const StepperNewSteps = [
  NewBookingSteps.SELECT_SERVICES,
  NewBookingSteps.SCHEDULE_APPOINTMENT,
  NewBookingSteps.ENTER_ACCOUNT_DETAILS,
  NewBookingSteps.COMPLETE_BOOKING,
];

export const StepperNewStepsMapping = {
  [NewBookingSteps.SELECT_SERVICES]: [BookingStep.SERVICES],
  [NewBookingSteps.SCHEDULE_APPOINTMENT]: [
    BookingStep.AVAILABILITY_AND_PROVIDER_SELECTION,
  ],
  [NewBookingSteps.ENTER_ACCOUNT_DETAILS]: [
    BookingStep.PHONE_NUMBER,
    BookingStep.CODE_INPUT,
    BookingStep.PHONE_CONFLICT,
  ],
  [NewBookingSteps.COMPLETE_BOOKING]: [
    BookingStep.CLIENT_DETAILS,
    BookingStep.COLLECT_CREDIT_CARD_INFO,
    BookingStep.RETURNING_CLIENT,
    BookingStep.RETURNING_CLIENT_EDIT_DETAILS,
    BookingStep.RETURNING_CLIENT_EDIT_CARD,
    BookingStep.FRONT_DESK_BOOKING,
  ],
};

type BookingStepperContainerProps = {
  children: ReactNode;
  step: BookingStep;
  customizationEnabled: boolean;
  customizationPrimaryColor: string;
  customizationTextColor: string;
  poweredByMoxie?: ReactNode;
};

export const BookingStepperContainer = ({
  children,
  step,
  customizationEnabled,
  customizationPrimaryColor,
  customizationTextColor,
  poweredByMoxie,
}: BookingStepperContainerProps) => {
  const theme = useTheme();
  const { isSelectiveBookingLink, selectiveBookingLink, medspaData } =
    useClientBookingContext();
  const handleInvalidSelectiveBookingLinkRedirection =
    useInvalidSelectiveBookingLinkRedirection();

  const isSelectiveBookingLinkInvalid = isInvalidSelectiveBookingLink(
    isSelectiveBookingLink,
    selectiveBookingLink,
    medspaData.serviceMenuItems
  );

  const discountCustomBookingLinkSummaryText = useMemo(() => {
    if (!selectiveBookingLink?.serviceDiscount) return null;
    /**
     * Custom booking links discounts are always service discounts
     */
    return getDiscountDetailsText({
      discountAppliesTo: DiscountType.SERVICE,
      discountValue: selectiveBookingLink.serviceDiscount.value,
      valueType: selectiveBookingLink.serviceDiscount.discountType,
    });
  }, [selectiveBookingLink]);

  const getSelectiveBookingLinkText = useMemo(() => {
    if (!selectiveBookingLink) return null;
    if (!selectiveBookingLink.serviceDiscount) return selectiveBookingLink.name;
    return `${selectiveBookingLink.name}. Get a ${discountCustomBookingLinkSummaryText}`;
  }, [selectiveBookingLink, discountCustomBookingLinkSummaryText]);

  /**
   * Handle invalid selective booking link redirection
   */
  useEffect(() => {
    if (isSelectiveBookingLinkInvalid) {
      handleInvalidSelectiveBookingLinkRedirection();
    }
  }, [
    isSelectiveBookingLinkInvalid,
    handleInvalidSelectiveBookingLinkRedirection,
  ]);

  const activeStep = useMemo(() => {
    const newStep = Object.keys(StepperNewStepsMapping).find((key) =>
      StepperNewStepsMapping[key].includes(step)
    );
    return newStep ? StepperNewSteps.indexOf(newStep as NewBookingSteps) : 0;
  }, [step]);

  const isActiveStep = useCallback(
    (stepIndex: number) => activeStep === stepIndex,
    [activeStep]
  );
  const isDoneStep = useCallback(
    (stepIndex: number) => activeStep > stepIndex,
    [activeStep]
  );

  const goToPreviousStep = useBookingGoBackButton(activeStep, step);

  if (step === BookingStep.BOOKED) {
    return <>{children}</>;
  }

  const getBorderColor = (index: number): string => {
    const doneBorderColor =
      customizationEnabled && customizationPrimaryColor
        ? alpha(customizationPrimaryColor, 0.4)
        : theme.palette.violet.light;

    const borderColors = {
      active: theme.palette.primary.main,
      done: doneBorderColor,
      inactive: theme.palette.grey[200],
    };

    if (isDoneStep(index)) return borderColors.done;
    if (isActiveStep(index)) return borderColors.active;
    return borderColors.inactive;
  };

  return (
    <>
      <Stack sx={{ mb: 3 }}>
        <Typography
          variant="labelSmall"
          sx={{
            color: "text.secondary",
            height: "30px",
            display: "flex",
            alignItems: "center",
            mb: 0.5,
          }}
        >
          {activeStep > 0 && (
            <IconButton sx={{ p: 0.5, mr: 1 }} onClick={goToPreviousStep}>
              <ArrowLeftIcon size={24} color={theme.palette.secondary.main} />
            </IconButton>
          )}
          {`Step ${activeStep + 1}`}
        </Typography>
        <Typography variant="h1" sx={{ mb: 1.5 }}>
          {StepperNewSteps[activeStep]}
        </Typography>

        <Stack flexDirection="row" gap={2}>
          {StepperNewSteps.map((step, index) => (
            <Box
              key={step}
              sx={{
                borderTopStyle: "solid",
                borderTopWidth: "4px",
                borderRadius: "8px",
                borderColor: getBorderColor(index),
                display: "flex",
                flex: "1 1 auto",
                transition: theme.transitions.create(["border-color"], {
                  duration: theme.transitions.duration.standard,
                }),
              }}
            />
          ))}
        </Stack>
        {isSelectiveBookingLink && (
          <AdditionalInfo variant="info" text={getSelectiveBookingLinkText} />
        )}
      </Stack>
      {children}
      <BookingNavBar
        buttonBackgroundColor={
          customizationEnabled ? customizationPrimaryColor : undefined
        }
        buttonTextColor={
          customizationEnabled ? customizationTextColor : undefined
        }
        poweredByMoxie={poweredByMoxie}
      />
    </>
  );
};
